import { useId } from "react";

import { cx } from "./ui";

/*
  eclwin88 identity.

  The mark is a casino chip: gold rim, eight crimson spots around it (the 8 of
  the name, counted on the edge) and a geometric double-eight on a near-black
  field. It is the same artwork as /favicon.svg, minus the gloss and halo — both
  disappear below ~64px and only cost paint time in the shell.

  Gradient ids are generated per instance so two marks on one page cannot
  collide, which is what breaks SVG gradients when a component is reused.
*/

export function BrandMark({
  size = 36,
  className,
}: {
  size?: number;
  className?: string;
}) {
  const uid = useId().replace(/:/g, "");
  const rim = `rim-${uid}`;
  const spot = `spot-${uid}`;
  const field = `field-${uid}`;
  const digit = `digit-${uid}`;

  return (
    <svg
      viewBox="0 0 512 512"
      width={size}
      height={size}
      className={cx("shrink-0", className)}
      role="img"
      aria-label="eclwin88"
    >
      <defs>
        <linearGradient id={rim} x1="0.12" y1="0" x2="0.86" y2="1">
          <stop offset="0" stopColor="#fff4d0" />
          <stop offset="0.18" stopColor="#f0cb72" />
          <stop offset="0.48" stopColor="#e7a740" />
          <stop offset="0.78" stopColor="#a9761b" />
          <stop offset="1" stopColor="#6f4a0e" />
        </linearGradient>
        <linearGradient id={spot} x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0" stopColor="#e05a46" />
          <stop offset="0.5" stopColor="#c34537" />
          <stop offset="1" stopColor="#6d221c" />
        </linearGradient>
        <radialGradient id={field} cx="0.5" cy="0.44" r="0.72">
          <stop offset="0" stopColor="#4a1017" />
          <stop offset="0.45" stopColor="#1c070b" />
          <stop offset="1" stopColor="#080304" />
        </radialGradient>
        <linearGradient id={digit} x1="0.15" y1="0" x2="0.68" y2="1">
          <stop offset="0" stopColor="#fff8dd" />
          <stop offset="0.26" stopColor="#f5d489" />
          <stop offset="0.58" stopColor="#e7a740" />
          <stop offset="1" stopColor="#b8821f" />
        </linearGradient>
      </defs>

      <circle cx="256" cy="256" r="254" fill={`url(#${rim})`} />
      <circle
        cx="256"
        cy="256"
        r="241"
        fill="none"
        stroke={`url(#${spot})`}
        strokeWidth="26"
        strokeDasharray="95 94.3"
        transform="rotate(-67.5 256 256)"
      />
      <circle cx="256" cy="256" r="230" fill="#2a0d0b" />
      <circle cx="256" cy="256" r="226" fill={`url(#${field})`} />
      <g
        transform="translate(256 256) scale(1.4) translate(-256 -256)"
        fill="none"
        stroke={`url(#${digit})`}
        strokeWidth="26"
      >
        <circle cx="190" cy="209" r="36" />
        <circle cx="190" cy="294" r="46" />
        <circle cx="322" cy="209" r="36" />
        <circle cx="322" cy="294" r="46" />
      </g>
    </svg>
  );
}

/* Mark plus wordmark, for the sidebar head and the sign-in card. */
export function BrandLockup({
  subtitle,
  size = 36,
  wordmarkClass = "text-[17px]",
}: {
  subtitle?: string;
  size?: number;
  wordmarkClass?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <BrandMark size={size} />
      <div className="min-w-0 leading-none">
        <p
          className={cx("display lowercase", wordmarkClass)}
          style={{
            background:
              "linear-gradient(120deg, var(--color-gold-light), var(--color-gold), var(--color-gold-deep))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          eclwin88
        </p>
        {subtitle ? <p className="eyebrow mt-1 truncate">{subtitle}</p> : null}
      </div>
    </div>
  );
}
